import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { infoToolTip, purpleRedirectIcon } from '../../../assets/hostedassets';
import LoadingSpinner from '../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SideDrawer from '../../../common/_custom/SideDrawer/SideDrawer';
import Tooltip from '../../../common/_custom/Tooltip/Tooltip';
import MatchSignalChips, { selectMatchSignalChips } from '../../../common/MatchStrength/MatchSignalChips';
import type { MatchEvidence } from '../../../common/MatchStrength/types';
import { isEmpty } from '../../../utils/utils';
import { RISK_MESSAGES_MAP, RISK_HEADER_KEYS, REGULATORY_RISK_SECTIONS, matchCountLabel } from '../RiskIndicatorConstants';
import {
    RegulatoryRiskDrawerCheckTypeConfig,
    RegulatoryRiskDrawerField,
    RegulatoryRiskFieldType,
    isMatched,
    formatScore,
    toDisplayString,
    toUrlList,
} from '../useRegulatoryRiskDrawerConfig';
import CollapsibleBody from '../../../common/_custom/CollapsibleBody/CollapsibleBody';
import styles from './RegulatoryRiskDrawer.module.scss';

// A regulatory hit, as returned by getRegulatoryRiskHits: config-driven fields keyed by
// rawKey, plus the same matchEvidence shape the legal-cases drawer already carries.
type RegulatoryRiskHit = Record<string, any> & {
    matchEvidence?: MatchEvidence;
};

interface RegulatoryRiskDrawerProps {
    checkType: string;
    entityName?: string;
    matches: RegulatoryRiskHit[];
    // RECUR-27829. When present, the body splits into Confirmed / Other Probable instead of
    // one flat list. Absent (older BE) => fall back to rendering `matches` flat.
    confirmedMatches?: RegulatoryRiskHit[];
    probableMatches?: RegulatoryRiskHit[];
    config?: RegulatoryRiskDrawerCheckTypeConfig;
    isLoading?: boolean;
    defaultChipLabel?: string;
    onClose: () => void;
}

// The noun rendered as "View <label> N" — an enum instead of bare 'Source'/'Image' literals.
enum LinkLabel {
    Source = 'Source',
    Image = 'Image',
}

// Long text values (e.g. Description) clamp to 4 lines with a View more / View less toggle.
// The toggle only appears when the text actually overflows the clamp — short cells are untouched.
const TruncatedText = ({ text }: { text: string }) => {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    // Reset expansion when the row is reused with new text (positional keys), so overflow is
    // measured against the clamped render, not a stale expanded one.
    useLayoutEffect(() => {
        setExpanded(false);
    }, [text]);

    useLayoutEffect(() => {
        if (expanded) return;
        const el = textRef.current;
        // measured against the clamped render (first paint): scrollHeight > clientHeight ⇒ >4 lines.
        if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [text, expanded]);

    return (
        <span className={styles.FieldValue}>
            <span ref={textRef} className={expanded ? styles.TextExpanded : styles.TextClamped}>
                {text}
            </span>
            {overflowing && (
                <button type="button" className={styles.ViewToggle} onClick={() => setExpanded((e) => !e)}>
                    {expanded ? 'View less' : 'View more'}
                </button>
            )}
        </span>
    );
};

const renderLinkGroup = (value: any, label: LinkLabel) => {
    const urls = toUrlList(value);
    if (urls.length === 0) return <span className={styles.FieldValue}>{toDisplayString(value)}</span>;
    if (urls.length === 1) {
        return (
            <a className={styles.FieldLink} href={urls[0]} target="_blank" rel="noopener noreferrer">
                <img className={styles.LinkIcon} src={purpleRedirectIcon} alt="" aria-hidden="true" />
                View {label}
            </a>
        );
    }
    return (
        <span className={styles.LinkGroup}>
            {urls.map((url, i) => (
                <a className={styles.FieldLink} href={url} target="_blank" rel="noopener noreferrer" key={`${url}-${i}`}>
                    <img className={styles.LinkIcon} src={purpleRedirectIcon} alt="" aria-hidden="true" />
                    View {label} {i + 1}
                </a>
            ))}
        </span>
    );
};

const renderFieldValue = (field: RegulatoryRiskDrawerField, value: any) => {
    switch (field.type) {
        case RegulatoryRiskFieldType.SourceLink:
            return renderLinkGroup(value, LinkLabel.Source);
        case RegulatoryRiskFieldType.ImageLink:
            return renderLinkGroup(value, LinkLabel.Image);
        default:
            // text renders as-is; line breaks preserved via CSS (white-space: pre-line).
            // Non-primitive values (arrays / objects, e.g. Associations) are coerced to a readable string.
            // Clamped to 4 lines with a View more / View less toggle when it overflows.
            return <TruncatedText text={toDisplayString(value)} />;
    }
};

const getFieldValue = (field: RegulatoryRiskDrawerField, hit: RegulatoryRiskHit): ReactNode => {
    // 'Other Parameters Matched': the config lists candidate params; the BE hit
    // holds each one's status. Show the labels of the ones that matched, as one row.
    if (field.type === RegulatoryRiskFieldType.MatchedParams) {
        const matchedLabels = (field.params ?? []).filter((p) => isMatched(hit?.[p.rawKey])).map((p) => p.label);
        if (matchedLabels.length === 0) return null;
        return <span className={styles.FieldValue}>{matchedLabels.join(', ')}</span>;
    }
    const value = hit?.[field.rawKey];
    if (isEmpty(value)) return null;
    return renderFieldValue(field, value);
};

// One hit: a disclosure header (caret + "Match N" + its matched-signal chips) over the
// config-driven field rows, reusing the same collapse mechanics as a match section. Its own
// `open` state lives here -- one useState per card instance, never inside a loop in the parent
// (repo rules 2+3) -- and starts expanded, matching today's "everything visible" behaviour.
const HitCard = ({
    hit,
    index,
    fields,
    chipLabel,
    nameMatchKey,
    drawerMountedAt,
}: {
    hit: RegulatoryRiskHit;
    index: number;
    fields: RegulatoryRiskDrawerField[];
    chipLabel: string;
    nameMatchKey?: string;
    drawerMountedAt: number;
}) => {
    const [open, setOpen] = useState(true);

    // The engine's own signal chips win when there's evidence to derive them from; the vendor's
    // config-driven chip is the legacy fallback for a hit the engine never scored.
    const signalChips = selectMatchSignalChips(hit.matchEvidence);
    const chipValue = nameMatchKey ? hit?.[nameMatchKey] : undefined;
    const showLegacyChip = signalChips.length === 0 && !isEmpty(chipValue);

    return (
        <div className={styles.HitCard}>
            <button type="button" className={styles.HitCardHeader} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                <span className={styles.MatchLabel}>
                    <span aria-hidden="true" className={open ? styles.SectionChevronOpen : styles.SectionChevron} />
                    Match {index + 1}
                </span>
                <MatchSignalChips chips={signalChips} />
                {showLegacyChip && (
                    <span className={styles.NameMatchChip}>
                        {chipLabel} {formatScore(chipValue)}
                    </span>
                )}
            </button>
            {/* Starts open inside a section that animates its own opening: replaying a second
                collapse animation within it would read as the card stuttering in. */}
            <CollapsibleBody
                bodyClassName={styles.MatchSectionBody}
                isOpen={open}
                drawerMountedAt={drawerMountedAt}
                animateInitialOpen={false}
            >
                {fields.map((field) => {
                    const fieldValue = getFieldValue(field, hit);
                    if (!fieldValue) return null;
                    return (
                        <div className={styles.FieldRow} key={field.rawKey}>
                            <span className={styles.FieldLabel}>{field.label}</span>
                            {fieldValue}
                        </div>
                    );
                })}
            </CollapsibleBody>
        </div>
    );
};

const RegulatoryRiskDrawer = ({
    checkType,
    entityName,
    matches,
    confirmedMatches,
    probableMatches,
    config,
    isLoading,
    defaultChipLabel = 'Name Matched',
    onClose,
}: RegulatoryRiskDrawerProps) => {
    /*
        This component is mounted only while the drawer is open, so its first render is the
        moment the drawer starts entering. CollapsibleBody measures the entrance against
        this to decide whether it still needs to wait.
    */
    const drawerMountedAtRef = useRef(performance.now());
    const title = config?.title || checkType;
    const chipLabel = config?.chipLabel || defaultChipLabel;
    const fields = config?.fields ?? [];
    const infoText = RISK_MESSAGES_MAP[checkType as RISK_HEADER_KEYS];
    // a hit with no displayable field (e.g. a clean/Green result echoing only the input) renders an empty card.
    // fields is empty while config is still loading (see the config-undefined test below) — don't filter then,
    // there's nothing yet to judge "content" against.
    // Signal chips count as content: MatchSignalChips renders in the card HEADER, independent of
    // `fields`, so a hit the engine scored can be fully displayable while every config field is
    // empty. Judging that hit on `fields` alone dropped it from the list AND from the section
    // counts below, hiding a real finding. An empty chip set still falls through to the field
    // check, so a clean result with nothing to show is filtered exactly as before.
    const hasContent = (hit: RegulatoryRiskHit) =>
        fields.length === 0 ||
        selectMatchSignalChips(hit.matchEvidence).length > 0 ||
        fields.some((field) => getFieldValue(field, hit) !== null);
    const renderableMatches = matches.filter(hasContent);

    /*
        RECUR-27829. Both section counts are the RENDERED lengths, not the BE's
        confirmedCount/probableCount — `hasContent` drops hits with nothing displayable, and
        pairing a BE count with a filtered list is the "pill said 3, drawer said 2" defect
        recorded in RECUR-24938.
    */
    const isSplit = Array.isArray(confirmedMatches) || Array.isArray(probableMatches);
    const confirmed = useMemo(() => (confirmedMatches ?? []).filter(hasContent), [confirmedMatches, fields]);
    const probable = useMemo(() => (probableMatches ?? []).filter(hasContent), [probableMatches, fields]);

    /*
        Confirmed opens; Probable stays collapsed behind it — a handful of identity matches
        should not be buried under fifty name-similarity guesses. With no confirmed matches
        there is nothing to bury, so Probable opens instead.
    */
    const [openConfirmed, setOpenConfirmed] = useState(true);
    const [openProbable, setOpenProbable] = useState(false);
    const confirmedCountKey = confirmed.length;
    useLayoutEffect(() => {
        setOpenConfirmed(true);
        setOpenProbable(confirmedCountKey === 0);
    }, [confirmedCountKey, checkType, entityName]);

    // The header badge counts rendered hits, never the BE's matchCount, so it can't disagree with
    // the list below it: confirmed.length + probable.length when split, renderableMatches
    // otherwise. Hidden at 0 so an empty/loading drawer never shows "0 Matches".
    const renderedMatchCount = isSplit ? confirmed.length + probable.length : renderableMatches.length;

    // Match numbering restarts within each section, matching the design ("Match 1" under
    // Confirmed even when Probable holds fifty more).
    const renderSection = (
        section: { title: string; pillBackground: string; pillColor: string },
        hits: RegulatoryRiskHit[],
        isOpen: boolean,
        onToggle: () => void,
    ) => (
        <div className={styles.MatchSection} key={section.title}>
            {/*
                Chevron, title and count sit in one left-aligned group. The title must not grow:
                giving it flex-grow is what pushed the count pill to the far right edge of the
                header, away from the heading it belongs to. The count comes from hits.length, the
                rendered rows — never a backend count, which is how the pill and the list came to
                disagree before (RECUR-24938). Uppercasing on the pill is presentational only, so
                the DOM text stays "2 Matches" for the accessible name and the tests.
            */}
            <button type="button" className={styles.MatchSectionHeader} onClick={onToggle} aria-expanded={isOpen}>
                <span className={isOpen ? styles.SectionChevronOpen : styles.SectionChevron} aria-hidden="true" />
                <span className={styles.SectionTitle}>{section.title}</span>
                <span className={styles.SectionCount} style={{ color: section.pillColor, backgroundColor: section.pillBackground }}>
                    {matchCountLabel(hits.length)}
                </span>
            </button>
            <CollapsibleBody bodyClassName={styles.MatchSectionBody} isOpen={isOpen} drawerMountedAt={drawerMountedAtRef.current}>
                {hits.map((hit, index) => (
                    <HitCard
                        key={index}
                        hit={hit}
                        index={index}
                        fields={fields}
                        chipLabel={chipLabel}
                        nameMatchKey={config?.nameMatchKey}
                        drawerMountedAt={drawerMountedAtRef.current}
                    />
                ))}
            </CollapsibleBody>
        </div>
    );

    return (
        <SideDrawer
            allowClose
            onClose={onClose}
            width="804px"
            showHeading
            heading={
                <div className={styles.HeaderTitle}>
                    <span className={styles.CheckTypeTitle}>{title}</span>
                    {!!infoText && (
                        // usePortal: HeaderTitle has overflow:hidden (for entity-name ellipsis), which
                        // clips a normally-rendered Tooltip popover. Portal escapes it (same pattern as
                        // MandateSharingDrawer / BasicDetailsSidebar tooltips inside other drawers).
                        <Tooltip content={infoText} direction="top" usePortal>
                            <img className={styles.InfoIcon} src={infoToolTip} alt="Information tooltip" />
                        </Tooltip>
                    )}
                    {!!entityName && (
                        <>
                            <span className={styles.Dot}>|</span>
                            <span className={styles.EntityName}>{entityName}</span>
                        </>
                    )}
                    {renderedMatchCount > 0 && <span className={styles.MatchCountBadge}>{matchCountLabel(renderedMatchCount)}</span>}
                </div>
            }
        >
            <div className={styles.Body}>
                {isLoading && (
                    <div className={styles.LoadingState}>
                        <LoadingSpinner color="#6021B3" height="1.5rem" />
                    </div>
                )}
                {!isLoading && renderableMatches.length === 0 && <div className={styles.EmptyState}>No adverse match found.</div>}
                {!isLoading && renderableMatches.length > 0 && isSplit && (
                    <>
                        {confirmed.length > 0 &&
                            renderSection(REGULATORY_RISK_SECTIONS.CONFIRMED, confirmed, openConfirmed, () => setOpenConfirmed((o) => !o))}
                        {probable.length > 0 &&
                            renderSection(REGULATORY_RISK_SECTIONS.PROBABLE, probable, openProbable, () => setOpenProbable((o) => !o))}
                    </>
                )}
                {!isLoading &&
                    !isSplit &&
                    renderableMatches.map((hit, index) => (
                        <HitCard
                            key={index}
                            hit={hit}
                            index={index}
                            fields={fields}
                            chipLabel={chipLabel}
                            nameMatchKey={config?.nameMatchKey}
                            drawerMountedAt={drawerMountedAtRef.current}
                        />
                    ))}
            </div>
        </SideDrawer>
    );
};

export default RegulatoryRiskDrawer;
